import { JamTask, JamTaskStatus } from '@/src/types/JamChallengeDetails';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, Button, ColumnLayout, Container } from '@amzn/awsui-components-react';
import { sum } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PointsContainer.module.scss';
import { config } from '@/src/config/app-config';

interface TaskPointContainerProps {
  task: JamTask | undefined;
  handleProgressCheck: () => Promise<any>;
  handleAiTaskAudit: () => Promise<any>;
  disableProgressButton: boolean;
  disableAuditButton: boolean;
  loadingAudit: boolean;
}

const TaskPointContainer: React.FC<TaskPointContainerProps> = ({
  task,
  handleProgressCheck,
  handleAiTaskAudit,
  disableProgressButton,
  disableAuditButton,
  loadingAudit,
}) => {
  const { t } = useTranslation();

  const TotalPenalty = React.useMemo(
    () =>
      task?.clues && task.clues.length > 0
        ? sum(task?.clues.filter((clue) => clue.used).map((clue) => clue.cluePenalty))
        : 0,
    [task?.clues]
  );

  const AvailablePoints = React.useMemo(() => {
    const Points = (task?.pointsPossible ?? 0) - (task?.pointsEarned ?? 0);
    return !!Points ? Points - TotalPenalty : 0;
  }, [task?.clues, TotalPenalty]);

  const showProgressCheckButton = task?.status !== JamTaskStatus.COMPLETE && task?.validatedByLambda;
  const showAiAuditButton = config.isGenAiIntegrationEnabled === true;
  const gridDefinition = [{ colspan: 10 }];
  if (showProgressCheckButton) {
    gridDefinition.push({ colspan: 2 });
  }

  return (
    <Container variant="default" className="dark-bg">
      <div className={styles.container}>
        <div className={styles.columns}>
          <ColumnLayout columns={3} variant="text-grid">
            <Box variant="div">
              <Box>{t(i18nKeys.myJams.challenges.details.task.possiblePoints)}</Box>
              <span className={styles.infoValue}>{task?.pointsPossible}</span>
            </Box>
            <Box variant="div">
              <Box>{t(i18nKeys.myJams.challenges.details.task.cluePenalty)}</Box>
              <span className={styles.infoValue}>{TotalPenalty}</span>
            </Box>
            <Box variant="div">
              <Box>{t(i18nKeys.myJams.challenges.details.task.PointsAvailable)}</Box>
              <span className={styles.infoValue}>{AvailablePoints}</span>
            </Box>
          </ColumnLayout>
        </div>
        <div className={styles.button}>
          {showProgressCheckButton && (
            <Box variant="div" margin={{ top: 'xs' }}>
              <Button variant="primary" disabled={disableProgressButton} onClick={() => void handleProgressCheck()}>
                {t(i18nKeys.myJams.challenges.details.task.buttons.checkMyProgress)}
              </Button>
            </Box>
          )}
          {showAiAuditButton && (
            <Box variant="div" margin={{ top: 'xs' }}>
              <Button
                variant="primary"
                data-testid="taskAuditButton"
                disabled={disableProgressButton || disableAuditButton}
                loading={loadingAudit}
                onClick={() => void handleAiTaskAudit()}
              >
                Have AI Check My Work
              </Button>
            </Box>
          )}
        </div>
      </div>
    </Container>
  );
};

export default TaskPointContainer;
